import { createColumnHelper, Row } from '@tanstack/react-table';

import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
import {
    KPI_VALUE_MEDIA_VIEWER_CLASSIC,
    KPI_VALUE_PERCENT
} from '@/utils/hooks/format/useFormatKpiValue';

// Utils
import { sortTableGroupKpiColumn } from '@/utils/table/sortKpiColumn';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';
import type { GroupGlobalTableAnalyticsFieldsType } from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';

import type { KPIDataType } from '@/lib/analytics/kpis/kpi.type';
import type { GroupGlobalTableConfigType } from '@/lib/analyticsGroup/tables/groupGlobalTableConfig.type';

import GroupCell from '@/components/base/Table/CompoundTable/TableCells/GroupCell';
import type { KpiCellProps } from '@/components/base/Table/CompoundTable/TableCells/KpiCell';
// Components
import KpiCell from '@/components/base/Table/CompoundTable/TableCells/KpiCell';
import SocialProfileCell from '@/components/base/Table/CompoundTable/TableCells/SocialProfileCell';
import { Trans } from '@/components/tools/translation';

type ColumnsVisibilityType = Record<GroupGlobalTableAnalyticsFieldsType, boolean>;
export type DataGroupGlobalTableRowAnalyticsType = {
    group?: SpGroupType;
    social_profile?: SocialProfileType;
} & Record<GroupGlobalTableAnalyticsFieldsType, KPIDataType>;

const defaultColumns = [{ id: 'avatar' }] as const;

type ColumnKpiType = {
    id: GroupGlobalTableAnalyticsFieldsType;
    header: string;
    format?: KpiCellProps['format'];
    show?: boolean;
};

const columnsKpi: ColumnKpiType[] = [
    { id: 'followers', header: 'analytics:followers' },
    { id: 'followers_evolution', header: 'analytics:kpi.instagram.followers_evolution' },
    { id: 'posts', header: 'analytics:kpi.instagram.total_posts' },
    { id: 'engagement', header: 'analytics:kpi.facebook.post_engagement' },
    { id: 'likes', header: 'analytics:likes' },
    { id: 'comments', header: 'analytics:kpi.instagram.comments' },
    { id: 'shares', header: 'analytics:kpi.instagram.shares' },
    { id: 'impressions', header: 'analytics:kpi.instagram.impressions_views' },
    { id: 'reach', header: 'analytics:kpi.instagram.reach' },
    {
        id: 'engagement_rate',
        header: 'analytics:kpi.instagram.engagement_rate',
        format: KPI_VALUE_PERCENT
    },
    {
        id: 'engagement_reach',
        header: 'analytics:kpi.instagram.engagement_reach',
        show: false,
        format: KPI_VALUE_PERCENT
    },
    {
        id: 'reels_posted',
        header: 'analytics:kpi.facebook.reels_posted',
        show: false
    },
    {
        id: 'reels_views',
        header: 'analytics:kpi.instagram.reels.reels_views_impressions',
        show: false
    },
    {
        id: 'reels_reach',
        header: 'analytics:kpi.facebook.reels_reach',
        show: false
    },
    {
        id: 'reels_saved',
        header: 'analytics:kpi.instagram.reels_saved',
        show: false
    },
    {
        id: 'stories_posted',
        header: 'analytics:kpi.instagram.stories.stories',
        show: false
    },
    {
        id: 'stories_views',
        header: 'analytics:kpi.instagram.stories.stories_views_impressions',
        show: false
    },
    {
        id: 'stories_reach',
        header: 'analytics:kpi.instagram.stories.total_stories_reach',
        show: false
    }
];

const columnHelper = createColumnHelper<DataGroupGlobalTableRowAnalyticsType>();

const ColumnsKpiAccessor = columnsKpi.map((column) => {
    const kpiFormat = 'format' in column ? column.format : KPI_VALUE_MEDIA_VIEWER_CLASSIC;
    return columnHelper.accessor(column.id, {
        id: column.id,
        header: () => <Trans i18nKey={column.header} />,
        cell: (info) => {
            const isGroup = 'group' in info.row.original;

            let hasStats = true;
            if ('social_profile' in info.row.original) {
                hasStats =
                    info.row.original.social_profile?.first_stats ||
                    info.row.original.social_profile?.full_stats;
            }

            return (
                <KpiCell
                    data={info.getValue()}
                    format={kpiFormat}
                    isHighlight={isGroup}
                    hasStats={hasStats}
                />
            );
        },
        meta: {
            kpi_format: kpiFormat
        },
        sortingFn: sortTableGroupKpiColumn<DataGroupGlobalTableRowAnalyticsType>
    });
});

const columnsVisibility = [...defaultColumns, ...columnsKpi].reduce((current, column) => {
    return { ...current, [column.id]: 'show' in column ? column.show : true };
}, {} as ColumnsVisibilityType);

const columns = [
    columnHelper.accessor((row) => ('group' in row ? row.group : row.social_profile), {
        id: 'avatar',
        cell: (info) => {
            const cellInfo = info.getValue();
            let cell = null;

            if ('group' in info.row.original) {
                const group = cellInfo as SpGroupType;
                cell = <GroupCell group={group} />;
            }

            if ('social_profile' in info.row.original) {
                const socialProfile = cellInfo as SocialProfileType;
                cell = <SocialProfileCell socialProfile={socialProfile} />;
            }

            return <div className="mx-2 flex items-center justify-center">{cell}</div>;
        },
        meta: {
            isEmptyHeader: true,
            headerCellClassName: 'w-16 min-w-[64px]',
            isSticky: true
        },
        enableHiding: false
    }),
    ...ColumnsKpiAccessor
];

const meta = {
    enableReorderingColumns: true,
    stickyRow: {
        isSticky: (row: Row<DataGroupGlobalTableRowAnalyticsType>) => 'group' in row.original
    }
} as const;

export const groupGlobalTableConfig: GroupGlobalTableConfigType<
    typeof columns,
    ColumnsVisibilityType,
    typeof meta
> = {
    id: 'base_group_global_table',
    type: 'groupGlobalTable',
    columns: columns,
    columnsVisibility: columnsVisibility,
    meta: meta
};
